import { motion } from "framer-motion";
import { Plug, HeartPulse, Calendar, ListTodo, FileText, Music, type LucideIcon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import type { Suggestion } from "@/lib/integrationSuggest";

/**
 * A proposta de integração, INLINE no fio da conversa do Espelho.
 *
 * Segue o WaSoftAskChip de propósito, e não o MirrorActionChip: aquele é
 * dirigido por `proposal.tool` e assume que vai escrever linha de domínio;
 * este não escreve nada, só leva para /settings/connections. Chip inline
 * também não disputa z-index com o overlay do avatar nem com o slot modal do
 * soft-ask de push — o usuário rola por cima se não quiser.
 *
 * A frase é montada com `tf`, com o módulo e a contagem reais interpolados:
 * "você registrou 12 treinos na mão nos últimos 30 dias — quer conectar o
 * Apple Saúde pra eu parar de perguntar?". É isso que faz a proposta parecer
 * o Espelho tendo reparado, e não um banner.
 */

const PROVIDER_ICON: Record<string, LucideIcon> = {
  apple_health: HeartPulse,
  health_connect: HeartPulse,
  google_calendar: Calendar,
  google_tasks: ListTodo,
  notion: FileText,
  spotify: Music,
};

const PROVIDER_COLOR: Record<string, string> = {
  apple_health: "hsl(0 72% 60%)",
  health_connect: "hsl(0 72% 60%)",
  google_calendar: "hsl(217 89% 61%)",
  google_tasks: "hsl(45 90% 55%)",
  notion: "hsl(0 0% 75%)",
  spotify: "hsl(141 72% 45%)",
};

interface IntegrationSuggestChipProps {
  suggestion: Suggestion;
  onAccept: () => void;
  onDecline: () => void;
}

export default function IntegrationSuggestChip({
  suggestion,
  onAccept,
  onDecline,
}: IntegrationSuggestChipProps) {
  const { t, tf } = useLanguage();
  const Icon = PROVIDER_ICON[suggestion.provider] ?? Plug;
  const color = PROVIDER_COLOR[suggestion.provider] ?? "hsl(187 72% 55%)";
  const providerName = t("settings.integrations." + suggestion.provider + ".name");

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="max-w-[86%] rounded-2xl rounded-bl-sm p-3.5 mt-1.5"
      style={{
        background: "hsl(222 18% 11%)",
        border: "1px solid hsl(187 72% 50% / 0.22)",
      }}
    >
      <div className="flex items-start gap-2.5 mb-2.5">
        <div
          className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: color + "24", border: "1px solid " + color + "47" }}
        >
          <Icon size={15} style={{ color }} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-display font-medium text-foreground/90 leading-snug">
            {tf(suggestion.reasonKey, { count: suggestion.signalCount })}
          </p>
          <p className="text-[11px] font-body text-foreground/55 leading-relaxed mt-0.5">
            {tf("mirror.integrationSuggest.body", { provider: providerName })}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          onClick={onAccept}
          className="w-full rounded-xl font-display font-semibold text-[13px] tracking-wide transition-all active:scale-[0.98]"
          style={{ minHeight: 42, background: "hsl(187 72% 45%)", color: "hsl(var(--background))" }}
        >
          {tf("mirror.integrationSuggest.accept", { provider: providerName })}
        </button>
        <button
          type="button"
          onClick={onDecline}
          className="w-full rounded-xl font-display font-medium text-[12px] tracking-wide transition-all active:scale-[0.98]"
          style={{
            minHeight: 38,
            background: "transparent",
            border: "1px solid hsl(222 18% 22%)",
            color: "hsl(218 14% 68%)",
          }}
        >
          {t("mirror.integrationSuggest.decline")}
        </button>
      </div>
    </motion.div>
  );
}
